import { Box, AppBar, Drawer, useMediaQuery, useTheme } from "@mui/material";
import {
	Routes,
	Route,
	useLocation,
	useNavigationType,
} from "react-router-dom";
import { Header } from "../components/navigation/Header";
import { SideMenu } from "../components/navigation/SideMenu";
import zIndex from "../designTokens/zIndex";
import Login from "../components/Auth/Login";
import VideoLogin from "../components/Auth/VideoLogin";
import Callback from "../components/Auth/Callback";
import Dashboard from "./Dashboard";
import LifetimeAnalytic from "./LifetimeAnalytic";
import SearchThreads from "./SearchThreads";
import FriendsInteraction from "./FriendsInteraction";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";
import MyAccount from "./MyAccount";
import Admin from "./Admin";
import AdminHome from "./AdminHome";
import AdminAvatarUpload from "./AdminAvatarUpload";
import AdminCollectionManager from "./AdminCollectionManager";
import AdminMonthlyMagazine from "./AdminMonthlyMagazine";
import { AdminRoute } from "../components/Auth/AdminRoute";
import { useState, useEffect, useMemo, useRef } from "react";
import MyPage from "./MyPage";
import UserPage from "./UserPage";
import ThreaderList from "./ThreaderList";
import CollectionPage from "./CollectionPage";
import PostThreads from "./PostThreads";
import ImageTestPage from "./ImageTestPage";
import MagazineIssue from "./MagazineIssue";
import MonthlyThreads from "./MonthlyThreads";
import SectionPage from "./SectionPage";
import {
	useThreadsState,
	useProgressiveLoading,
} from "../contexts/ThreadsContext";
import { useUser } from "../contexts/UserContext";
import { useWritingMode } from "../contexts/WritingModeContext";
import DataLoader from "../components/DataLoader";
import { useTranslation } from "react-i18next";
import AdminManualLifetimeThreads from "./AdminManualLifetimeThreads";
import AdminCreateCollection from "./AdminCreateCollection";
import AdminTaskLogs from "./AdminTaskLogs";
import AdminVisualization from "./AdminVisualization";
import AdminCafeManager from "./AdminCafeManager";
import ThreadsCafeMap from "./ThreadsCafeMap";
import Pricing from "./Pricing";
import AdminTextAttachmentBackfill from "./AdminTextAttachmentBackfill";
import AdminThreadsPeople from "./AdminThreadsPeople";
import ThreadsPeople from "./ThreadsPeople";
import Bookmarks from "./Bookmarks";
import Ideation from "./Ideation";
import IdeationEdit from "./IdeationEdit";
import YearEndPage from "./YearEndPage";
import YearInReviewPage from "./YearInReviewPage";
import NetworkGraphTest from "./NetworkGraphTest";
import LetterTetris from "./letter-tetris";

export const AppPresenter = () => {
	const { t } = useTranslation();

	// 사이드바 레이아웃 상수
	const SIDEBAR_WIDTH = 256;
	const SIDEBAR_LEFT_OFFSET = 16;
	const SIDEBAR_PADDING = 16;
	const MAIN_MARGIN_LEFT =
		SIDEBAR_WIDTH + SIDEBAR_LEFT_OFFSET + SIDEBAR_PADDING; // 256 + 16 + 16 = 288

	// UserContext에서 상태 가져오기
	const { profile, isLogin, userLoading, error } = useUser();

	// WritingMode Context에서 상태 가져오기
	const { isFocusMode, isTransitioning } = useWritingMode();

	// Navigation and UI states
	const [drawerOpen, setDrawerOpen] = useState(false);
	const toggle = () => setDrawerOpen(!drawerOpen);
	const close = () => setDrawerOpen(false);
	const theme = useTheme();
	const isXL = useMediaQuery(theme.breakpoints.down("xl")); // xl 이하 (1440px 이하)
	const location = useLocation();
	const navType = useNavigationType();
	const prevPathRef = useRef(location.pathname);
	const isPublicRoute =
		location.pathname.startsWith("/user/") ||
		location.pathname.startsWith("/collection/") ||
		location.pathname.startsWith("/monthly-threads") ||
		location.pathname.startsWith("/threads-cafe-map") ||
		location.pathname.startsWith("/people") ||
		location.pathname.startsWith("/year-end");

	// 자체 로딩 UI가 있는 페이지에서는 글로벌 로딩 숨기기
	const shouldHideGlobalLoading =
		location.pathname.startsWith("/collection/") ||
		location.pathname.startsWith("/monthly-threads") ||
		location.pathname.startsWith("/threads-cafe-map") ||
		location.pathname.startsWith("/people") ||
		location.pathname.startsWith("/year-end");

	// PostThreads 페이지인지 확인 (수정 모드 포함)
	const isPostThreadsPage =
		location.pathname === "/post-thread" ||
		location.pathname.startsWith("/post-thread/scheduled/") ||
		location.pathname.startsWith("/post-thread/draft/");

	// PostThreads 페이지에서 사이드바를 항상 숨기는 페이지들
	const shouldHideSidebarPages = isPostThreadsPage;

	// 네비게이션 상태 통합 계산
	const navigationState = useMemo(() => {
		const isMobile = isXL; // XL 이하
		const isDesktop = !isXL; // XL 초과

		// 사이드메뉴 표시 조건 - 컬렉션 페이지에서는 모바일에서만 drawer 표시
		const isCollectionRoute = location.pathname.startsWith("/collection/");
		const isMonthlyThreadsRoute =
			location.pathname.startsWith("/monthly-threads");
		const isThreadsCafeMapRoute =
			location.pathname.startsWith("/threads-cafe-map");
		const isThreadsPeopleRoute = location.pathname.startsWith("/people");
		const shouldShowSidebar =
			isLogin &&
			(!isPublicRoute || (isCollectionRoute && isMobile)) &&
			!isMonthlyThreadsRoute &&
			!isThreadsCafeMapRoute &&
			!isThreadsPeopleRoute &&
			!shouldHideSidebarPages;

		// 숨김 라우트(월간/카페)
		const isHiddenSidebarRoute = isMonthlyThreadsRoute || isThreadsCafeMapRoute || isThreadsPeopleRoute;
		// 모바일이거나 숨김 라우트면 임시 드로어 호출 가능
		const allowTemporaryDrawer = isLogin && (isMobile || isHiddenSidebarRoute);

		// Drawer 설정
		const drawerVariant = shouldShowSidebar
			? (isMobile ? "temporary" : "persistent")
			: "temporary";
		const shouldDrawerOpen = shouldShowSidebar
			? (isMobile ? drawerOpen : true)
			: drawerOpen;
		// 렌더: 일반 라우트(persistent) 또는 임시 드로어 허용 시 렌더
		const shouldRenderSidebar = shouldShowSidebar || allowTemporaryDrawer;

		return {
			isMobile,
			isDesktop,
			shouldShowSidebar,
			shouldRenderSidebar,
			drawerVariant,
			shouldDrawerOpen,
			isHiddenSidebarRoute,
		};
	}, [
		isXL,
		isLogin,
		isPublicRoute,
		isPostThreadsPage,
		drawerOpen,
		shouldHideSidebarPages,
	]);

	// ThreadsContext에서 모든 lifetime 데이터 가져오기
	const { lifetimeThreadsList, isLifetimeLoading, loadingProgress } =
		useThreadsState();

	// 점진적 로딩 훅
	const { progressiveState } = useProgressiveLoading();

	// breakpoint 변경 시 drawer 상태 관리
	useEffect(() => {
		// 모바일에서 데스크톱으로 변경되거나,
		// 페이지 라우트가 변경될 때 drawer를 닫음
		setDrawerOpen(false);
	}, [isXL, location.pathname]);

	// MonthlyThreads 관련 페이지 PUSH 이동 시 스크롤 초기화
	useEffect(() => {
		const prev = prevPathRef.current;
		const curr = location.pathname;
		prevPathRef.current = curr;

		// PUSH 이동일 때만 스크롤 초기화 (뒤로가기/앞으로가기는 제외)
		if (navType === "PUSH") {
			const isMonthlyRoot =
				prev === "/monthly-threads" || prev === "/monthly-threads/";
			const isMonthlyIssue = /^\/monthly-threads\/\d{4}-\d{2}$/.test(curr);
			const isSectionPage = /^\/monthly-threads\/\d{4}-\d{2}\/section\/\d+$/.test(curr);

			// MonthlyThreads root -> Issue 이동 OR 어디서든 SectionPage로 이동 시 스크롤 0
			if ((isMonthlyRoot && isMonthlyIssue) || isSectionPage) {
				const currentState = window.history.state || {};
				window.history.replaceState(
					{ ...currentState, scrollY: 0, path: curr },
					"",
					curr + location.search
				);
				requestAnimationFrame(() => window.scrollTo(0, 0));
			}
		}
	}, [location.pathname, navType]);

	// ThreadsCafeMap / Monthly Threads 페이지 여부 확인
	const isThreadsCafeMapPage =
		location.pathname.startsWith("/threads-cafe-map");
	const isMonthlyThreadsPage = location.pathname.startsWith("/monthly-threads");
	const isThreadsPeoplePage = location.pathname.startsWith("/people");

	// 현재 로딩 단계에 따른 메시지 결정
	const getCurrentLoadingMessage = () => {
		if (userLoading) {
			return t("loading.user");
		}

		if (isLogin && isLifetimeLoading) {
			// ThreadsContext의 loadingProgress 상태 활용
			switch (loadingProgress.stage) {
				case "cache-check":
				case "cached":
				case "updating":
					return t("loading.threads_data");
				case "insights":
					return t("loading.insights_data");
				case "analyzing":
					return t("loading.analyzing_data");
				case "complete":
					return t("loading.complete");
				default:
					return t("loading.threads_data");
			}
		}

		return t("loading.essential_data");
	};

	// 서브 메시지 결정 (진행 상황 표시)
	const getCurrentSubMessage = () => {
		if (isLogin && isLifetimeLoading && loadingProgress.subMessage) {
			return loadingProgress.subMessage;
		}
		return null;
	};

	// 글로벌 로딩 상태 계산
	const globalLoading =
		(userLoading || (isLogin && isLifetimeLoading)) && !shouldHideGlobalLoading;

	// 점진적 로딩 모드 사용 여부 결정
	const useProgressiveMode = useMemo(() => {
		// 로그인 상태이고, 라이프타임 데이터를 로딩 중이며, 점진적 로딩 상태가 활성화된 경우
		return (
			isLogin &&
			isLifetimeLoading &&
			progressiveState.stage !== "initial" &&
			!shouldHideGlobalLoading
		);
	}, [
		isLogin,
		isLifetimeLoading,
		progressiveState.stage,
		shouldHideGlobalLoading,
	]);

	// firstThreadDate 메모이제이션으로 불필요한 정렬 방지
	const firstThreadDate = useMemo(() => {
		if (!lifetimeThreadsList || lifetimeThreadsList.length === 0) {
			return null;
		}
		// 가장 빠른 날짜 찾기 (전체 정렬 대신 reduce 사용)
		return lifetimeThreadsList.reduce((earliest, thread) => {
			const threadDate = new Date(thread.timestamp);
			const earliestDate = new Date(earliest);
			return threadDate < earliestDate ? thread.timestamp : earliest;
		}, lifetimeThreadsList[0].timestamp);
	}, [lifetimeThreadsList]);

	return (
		<>
			{/* 글로벌 데이터 로딩 표시 */}
			<DataLoader
				msg={getCurrentLoadingMessage()}
				subMessage={getCurrentSubMessage()}
				isLoad={globalLoading}
				showElapsedTime={isLogin && isLifetimeLoading}
				isLogin={isLogin}
				isXL={isXL}
				isPublicRoute={isPublicRoute}
				useProgressiveMode={useProgressiveMode}
			/>

			<Box sx={{ display: "flex" }}>
				<AppBar
					position="fixed"
					sx={{
						height: "64px",
						backgroundColor: "transparent",
						boxShadow: "none",
						zIndex: (theme) => theme.zIndex.appBar,
					}}
				>
					{/* position for GNB */}
					<Header
						userData={profile}
						isLogin={isLogin}
						opened={drawerOpen}
						onToggleNav={toggle}
						isPostThreadsPage={isPostThreadsPage}
						canOpenDrawer={
							isLogin &&
							(navigationState.isMobile || navigationState.isHiddenSidebarRoute)
						}
					/>
				</AppBar>
				{navigationState.shouldRenderSidebar && (
					<Drawer
						variant={navigationState.drawerVariant}
						anchor="left"
						open={navigationState.shouldDrawerOpen}
						onClose={
							navigationState.drawerVariant === "temporary"
								? () => setDrawerOpen(false)
								: undefined
						}
						transitionDuration={300}
						SlideProps={{
							timeout: 300,
						}}
						sx={{
							flexShrink: 0,
							display: "block",
							"& .MuiDrawer-paper": {
								width:
									navigationState.drawerVariant === "temporary"
										? "100vw"
										: SIDEBAR_WIDTH,
								boxSizing: "border-box",
								display: "flex",
								flexDirection: "column",
								backgroundColor:
									navigationState.drawerVariant === "temporary"
										? "rgba(255, 255, 255, 0.95)"
										: "transparent",
								backdropFilter:
									navigationState.drawerVariant === "temporary"
										? "blur(10px)"
										: "none",
								border: "none",
								top: navigationState.drawerVariant === "temporary" ? 0 : 88, // 오버레이는 전체
								left:
									navigationState.drawerVariant === "temporary"
										? 0
										: SIDEBAR_LEFT_OFFSET,
								height:
									navigationState.drawerVariant === "temporary"
										? "100dvh"
										: "calc(100dvh - 88px)", // 데스크톱에서는 헤더 + 오버레이 높이 제외
								overflow: "hidden",
								zIndex: zIndex.sideMenu,
							},
							"& .MuiDrawer-modal": {
								zIndex: zIndex.sideMenu,
							},
						}}
					>
						{profile && (
							<Box
								sx={{
									flex: 1,
									minHeight: 0,
									overflowY: "auto",
									WebkitOverflowScrolling: "touch",
								}}
							>
								<SideMenu
									userData={profile}
									toggle={toggle}
									forceMobileView={navigationState.drawerVariant === "temporary"}
								/>
							</Box>
						)}
					</Drawer>
				)}
				<Box
					component="main"
					sx={{
						flexGrow: 1,
						paddingTop: isThreadsCafeMapPage ? 0 : "64px",
						marginLeft:
							navigationState.shouldShowSidebar && navigationState.isDesktop
								? `${MAIN_MARGIN_LEFT}px`
								: 0,
						display: "flex",
						flexDirection: "column",
						alignItems:
							isThreadsCafeMapPage || isMonthlyThreadsPage || isThreadsPeoplePage
								? "stretch"
								: "center",
						overflowX: isThreadsCafeMapPage ? "hidden" : "visible",
						overflowY: "visible",
						transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
					className="app-main"
				>
					<Routes>
						{/* Public Routes - Accessible to all users */}
						<Route index path={"/"} element={<Login />} />
						<Route path="/video-login" element={<VideoLogin />} />
						<Route path="/callback" element={<Callback />} />
						<Route path="/terms" element={<TermsOfService />} />
						<Route path="/privacy" element={<PrivacyPolicy />} />
						<Route path="/user/:userID" element={<UserPage />} />
						<Route
							path="/admin/threads-people"
							element={
								<AdminRoute>
									<AdminThreadsPeople />
								</AdminRoute>
							}
						/>
						<Route path="/people" element={<ThreadsPeople />} />
						<Route
							path="/collection/:collectionId"
							element={<CollectionPage isMe={false} />}
						/>
						<Route
							path="/monthly-threads/:issueId/section/:sectionIndex"
							element={<SectionPage />}
						/>
						<Route
							path="/monthly-threads/:issueId"
							element={<MagazineIssue />}
						/>
						<Route path="/monthly-threads" element={<MonthlyThreads />} />
						<Route path="/threads-cafe-map" element={<ThreadsCafeMap />} />
						{/* Year-End Routes */}
						<Route path="/year-end" element={<YearEndPage />} />
						<Route path="/year-end/:id" element={<YearEndPage />} />
						<Route path="/year-in-review" element={<YearInReviewPage />} />

						{/* Private Routes - Require authentication */}
						<Route
							index
							path={"/dashboard"}
							element={
								<Dashboard
									profile={profile}
									userLoading={userLoading}
									error={error}
								/>
							}
						/>
						<Route
							path="/analytic"
							element={<LifetimeAnalytic firstThreadDate={firstThreadDate} />}
						/>
						<Route path="/search" element={<SearchThreads />} />
						<Route path="/bookmarks" element={<Bookmarks />} />
						<Route path="/letter-tetris" element={<LetterTetris />} />
						<Route
							path="/friends-interaction"
							element={<FriendsInteraction />}
						/>
						<Route
							path="/account"
							element={<MyAccount firstThreadDate={firstThreadDate} />}
						/>
						<Route
							path="/admin"
							element={
								<AdminRoute>
									<AdminHome />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/user-list"
							element={
								<AdminRoute>
									<Admin />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/avatar-upload"
							element={
								<AdminRoute>
									<AdminAvatarUpload />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/collection-manager"
							element={
								<AdminRoute>
									<AdminCollectionManager />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/monthly-magazine"
							element={
								<AdminRoute>
									<AdminMonthlyMagazine />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/manual-lifetime-threads"
							element={
								<AdminRoute>
									<AdminManualLifetimeThreads />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/create-collection"
							element={
								<AdminRoute>
									<AdminCreateCollection />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/task-logs"
							element={
								<AdminRoute>
									<AdminTaskLogs />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/visualization"
							element={
								<AdminRoute>
									<AdminVisualization />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/cafe-manager"
							element={
								<AdminRoute>
									<AdminCafeManager />
								</AdminRoute>
							}
						/>
						<Route
							path="/admin/text-attachment-backfill"
							element={
								<AdminRoute>
									<AdminTextAttachmentBackfill />
								</AdminRoute>
							}
						/>
						<Route
							path="/my-page"
							element={<MyPage firstThreadDate={firstThreadDate} />}
						/>
						<Route
							path="/my-collection"
							element={<MyPage firstThreadDate={firstThreadDate} />}
						/>
						<Route
							path="/my-collection/:collectionId"
							element={<CollectionPage isMe={true} />}
						/>
						<Route path="/threaders" element={<ThreaderList />} />
						<Route path="/post-thread" element={<PostThreads />} />
						<Route
							path="/post-thread/scheduled/:id"
							element={<PostThreads />}
						/>
						<Route path="/post-thread/draft/:id" element={<PostThreads />} />
						<Route path="/ideation" element={<Ideation />} />
						<Route path="/ideation/:id" element={<IdeationEdit />} />
						<Route path="/pricing" element={<Pricing />} />
						<Route path="/image-test" element={<ImageTestPage />} />
						<Route path="/network-graph-test" element={<NetworkGraphTest />} />
					</Routes>
				</Box>
			</Box>
		</>
	);
};
